/**
 * Catalog of TV games — exposed via GET /api/games and room control APIs.
 */

/** Глобальный перечень id игр (для рекордов, API, БД). Добавляйте сюда и в GAMES. */
export const GameId = {
  FruitNinjaRemote: 'fruit-ninja-without-score',
  TargetAttack: 'target-attack',
  CatchPose: 'catch-pose',
  LegKick: 'leg-kick',
  Runner: 'runner',
  Iq: 'iq',
  BirdFlight: 'bird-flight',
  ChefMaster: 'chef-master',
  FruitSliceWeb: 'fruit-slice-web',
  SkyRace: 'sky-race',
  LaserDodge: 'laser-dodge',
  /** IQ как веб-игра на ТВ (GEN-19048); старый нативный IQ — `Iq`. */
  IqWeb: 'iq-web',
} as const;

export type GameIdEnum = (typeof GameId)[keyof typeof GameId];

export interface GameDefinition {
  readonly id: GameIdEnum;
  readonly title: string;
  readonly description: string;
}

export const GAMES: readonly GameDefinition[] = [
  {
    id: GameId.FruitNinjaRemote,
    title: 'Fruit Slice',
    description: 'Trajectory and fruit positions come only from native WebRTC JSON (no local spawn).',
  },
  {
    id: GameId.TargetAttack,
    title: 'Target Attack',
    description: 'Hit targets from native WebRTC JSON — Classic / Arcade, 1–2 players.',
  },
  {
    id: GameId.CatchPose,
    title: 'Catch Pose',
    description: 'TV receives only remote video via native WebRTC track from Swift/Kotlin.',
  },
  {
    id: GameId.LegKick,
    title: 'Leg Kick',
    description: 'Hit targets with kicks — same engine as Target Attack, 1–2 players.',
  },
  {
    id: GameId.Runner,
    title: 'Runner',
    description: 'Endless runner game controlled via native WebRTC frames.',
  },
  {
    id: GameId.Iq,
    title: 'IQ',
    description: 'Quiz game — answer questions by touching the correct answer button.',
  },
  {
    id: GameId.BirdFlight,
    title: 'Bird Flight',
    description: '3D flight rendered on TV; the phone sends only pose points (shoulders, wrists) via WebRTC.',
  },
  {
    id: GameId.ChefMaster,
    title: 'Chef Master',
    description: 'Burger kitchen rendered on TV; the phone sends only hand joints (13 per hand) via WebRTC.',
  },
  {
    id: GameId.FruitSliceWeb,
    title: 'Fruit Slice Web',
    description: 'Fruit Slice rendered on TV (fruits, physics, score); the phone sends only fingertips via WebRTC.',
  },
  {
    id: GameId.SkyRace,
    title: 'Sky Race',
    description: 'Flying runner rendered on TV; the phone sends only pose points (shoulders, wrists) via WebRTC.',
  },
  {
    id: GameId.LaserDodge,
    title: 'Laser Dodge',
    description: 'Dodge laser zones; the phone runs the rules, the TV draws zones, score and skeleton.',
  },
  {
    id: GameId.IqWeb,
    title: 'IQ',
    description: 'Quiz page rendered on TV; the phone picks topic and difficulty and sends pose points via WebRTC.',
  },
] as const;

const GAME_IDS = new Set<string>(GAMES.map((g) => g.id));

/**
 * Resolves mobile/API variants (case, snake_case) to a canonical id from {@link GAMES}.
 * Examples: `target_attack` → `target-attack`, `FRUIT_NINJA_REMOTE` → `fruit-ninja-without-score`.
 */
export function resolveGameId(raw: string): GameIdEnum | null {
  const t = raw.trim();
  if (!t) return null;
  if (GAME_IDS.has(t)) return t as GameIdEnum;
  const kebab = t.toLowerCase().replace(/_/g, '-');
  if (GAME_IDS.has(kebab)) return kebab as GameIdEnum;
  return null;
}

export function isValidGameId(id: string): boolean {
  return resolveGameId(id) !== null;
}

export function getGameOrNull(id: string): GameDefinition | null {
  return GAMES.find((g) => g.id === id) ?? null;
}
